import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import ClientLicenceService from "../services/ClientLicenceService";
import CondominioService from "../services/CondominioService";
import { useAuth } from "./auth";
import UserService from "../services/UserService";
import { useBackup } from "./backup";
import { filterAllowedRoutes, routesData } from "../components/MenuBar/MenuBarIdentity";
import { mergeArraysFromItemFields, min } from "../utils/array";
import { subscribe, unsubscribe } from "../utils/events";
import { wait } from "../utils/timer";

//context que armazena dados relativos ao perfil de operador
const UserStoreContext = createContext({
	changeSelectedCondominio: () => { },
	changeSelectedLicence: () => { },
	getUrlPreference: () => { },
	hasHierarchyAccess: () => { },
	selectedCondominio: 0,
	selectedCondominioObj: {},
	selectedLicenceId: null,
	selectedLicenceObj: {},
	selectingUnit: true,
	condominiums: [],
	licences: [],
	monitoringShowCondoCommands: false,
	clientLicenses: [],
	currentAddress: null,
	geolocation: null,
});

let lastUserId = null;
let lastOrganizationId = null;

export const UserStoreProvider = ({ children }) => {
	const [selectedCondominio, setSelectedCondominio] = useState(0);
	const [selectedCondominioObj, setSelectedCondominioObj] = useState(null);
	const [condominiums, setCondominiums] = useState([]);
	const [selectedLicenceId, setSelectedLicenceId] = useState(null);
	const [selectedLicenceObj, setSelectedLicenceObj] = useState({});
	const [monitoringShowCondoCommands, setMonitoringShowCondoCommands] =
		useState(true);
	const [licences, setLicences] = useState([]);
	const { user, storeUser, storeToken, accessToken, can } = useAuth();
	const { clearBackup } = useBackup();
	const [clientLicenses, setClientLicenses] = useState([]);
	const [geolocation, setGeolocation] = useState(null);
	const [currentAddress, setCurrentAddress] = useState(null);
	const [selectingUnit, setSelectingUnit] = useState(false);

	const clear = () => {
		setSelectedCondominio(null)
		setSelectedCondominioObj(null)
		setSelectedLicenceId(null)
		setSelectedLicenceObj(null)
	}

	const changeSelectedCondominio = async (id) => {
		const response = await UserService.selectUnit(id);
		storeToken(response.data?.accessToken ?? accessToken);
		if (!response) {
			return;
		}
		const responseUser = await UserService.getUserData();
		storeUser({
			userData: {
				...responseUser.data,
				selectedUnitId: `${id}`,
			}
		});
	};

	const changeSelectedLicence = (id) => {
		setSelectedLicenceId(id);
		const licenceFound = licences.find((licence) => licence.id === id);
		setSelectedLicenceObj(licenceFound);
	};

	const storeOperatorCondominiums = async (user) => {
		const response = await CondominioService.listCondominiumsByOperatorId(
			user.userData.person.id,
		);
		console.log(`[USER_STORE] Unidades disponíveis: `, response.data);
		setCondominiums(response.data);
		clearBackup?.();
		return response.data;
	};

	const storeOperatorLicences = async (selectedCondominio) => {
		const response =
			await ClientLicenceService.listCondominiumLicencesAndAccess(
				selectedCondominio,
			);
		setLicences(response.data);
		setSelectedLicenceId(parseInt(response.data[0]?.id));
		setSelectedLicenceObj(response.data[0]);
	};

	const handleMonitoringShowCondoCommands = (checked) => {
		setMonitoringShowCondoCommands(checked);
	};

	const filterWithLicensesUnits = () => {
		return condominiums?.filter(unit => licences.some(license => license.id_condominium === Number(unit.id)));
	}

	function getGeolocation() {
		function success(position) {
			const latitude = position.coords.latitude;
			const longitude = position.coords.longitude;
			setGeolocation({ latitude, longitude });
		}

		function error() {
			console.error("Unable to retrieve your location");
			setGeolocation(null);
		}

		if (!navigator.geolocation) {
			console.error("Geolocation is not supported by your browser");
			setGeolocation(null);
		} else {
			navigator.geolocation.getCurrentPosition(success, error, {
				enableHighAccuracy: true
			});
		}
	}

	async function loadCondomiumsData() {
		setSelectingUnit(true);
		console.log('selected condo changed...');
		const _condominiums = await storeOperatorCondominiums(user);
		const abortController = new AbortController();
		const getClientsLicenses = async (controller) => {
			const response = await ClientLicenceService.listClientsAllocatedLicence(
				user.userData.selectedOrganizationId,
				controller
			);
			if (!response.data) return;
			const clientLicensesResponse = response.status !== 200 ? [] : response.data;
			setClientLicenses(clientLicensesResponse);

			if (Array.isArray(clientLicensesResponse)) {
				let firstUnitByOrg = _condominiums.find(
					unit => (
						user.userData.selectedOrganizationId === unit.organizationId &&
						(
							unit.parentId === null ||
							clientLicensesResponse.find(clientLicense => clientLicense.id_condominium && Number(clientLicense.id_condominium) === Number(unit.id))
						)
					)
				);
				if (!firstUnitByOrg) {
					const selectedOrgsUnits = _condominiums.filter(unit => unit.organizationId === user.userData.selectedOrganizationId);
					firstUnitByOrg = min(selectedOrgsUnits, 'level');
				}

				const unit = _condominiums.find(unit => unit.id === user.userData.selectedUnitId) ?? firstUnitByOrg;
				if (!user.userData.selectedUnitId || Number(user.userData.selectedUnitId) !== Number(unit.id)) {
					await changeSelectedCondominio(parseInt(unit.id));
				}
				if (selectedCondominio !== unit.id) {
					setSelectedCondominio(parseInt(unit.id));
					setSelectedCondominioObj(unit);
				}
			}
			return clientLicensesResponse;
		};
		if (user) {
			await getClientsLicenses(abortController);
		}
		setSelectingUnit(false);
	}

	useEffect(() => {
		if (!user) return;
		if (user?.userData?.selectedOrganizationId) {
			console.log(user);
			loadCondomiumsData();
		}
		getGeolocation();
		function onSseReceived(data) {
			if (data.action === 'licenseUpdated') {
				setLicences(licences.map(license => {
					if (license.id === data.content.licenseData.id) {
						return {
							...license,
							...data.content.licenseData,
						}
					}
					return license;
				}));
				if (selectedLicenceId === data.content.licenseData.id) {
					setSelectedLicenceId(data.content.licenseData.id);
					setSelectedLicenceObj({
						...selectedLicenceObj,
						...data.content.licenseData,
					});
				}
			}
			if (
				['unitCreated', 'unitDeleted', 'licenseUpdated'].includes(data.action)
			) {
				loadCondomiumsData();
			}
			if (
				data.action === 'unitUpdated' &&
				data.content.unit
			) {
				changeSelectedCondominio(parseInt(data.content.unit.id));
			}
		}
		subscribe('onSseReceived', onSseReceived);
		return () => unsubscribe('onSseReceived', onSseReceived);
	}, [user])

	useEffect(() => {
		if (selectedCondominio) {
			storeOperatorLicences(selectedCondominio);
			if (selectedCondominioObj.address) {
				setCurrentAddress(selectedCondominioObj.address);
			} else {
				(async () => {
					const response = await CondominioService.loadUnitAddress(selectedCondominio);
					if (response?.data && response.data.id) {
						setCurrentAddress(response.data);
					}
				})();
			}
		}
	}, [selectedCondominio]);

	const getUrlPreference = useCallback(() => {
		const urlPreference = user?.userData?.urlPreference;

		const allowedRoutes = filterAllowedRoutes(
			user.userData,
			licences,
			selectedLicenceObj,
			mergeArraysFromItemFields(routesData, 'items'),
			can
		);
		if (urlPreference) {
			const urlInContext = allowedRoutes.find(allowedRoute => allowedRoute.route.replace('..', '') === urlPreference);
			if (urlInContext) {
				return urlPreference;
			}
		}

		return allowedRoutes[0]?.route?.replace('..', '') ?? '/';
	}, [user, can, licences, selectedLicenceObj]);

	function hasHierarchyAccess(unitId) {
		if (user.userData.isSuperAdmin || user.userData.hasRootAccess) return true;
		const targetUnit = condominiums.find(c => c.id === unitId);
		if (!targetUnit) return false;
		return user.userData.person.authorityLevels
			.some(level => (level.unitId === targetUnit.id && level.hierarchy) || (targetUnit.fullHierarchyPath.startsWith(level.authorityPath) && level.hierarchy));
	}

	return (
		<UserStoreContext.Provider
			value={{
				changeSelectedCondominio,
				changeSelectedLicence,
				handleMonitoringShowCondoCommands,
				monitoringShowCondoCommands,
				licences,
				condominiums,
				selectedCondominio,
				setSelectedCondominio,
				setSelectedCondominioObj,
				selectedLicenceId,
				selectedLicenceObj,
				selectedCondominioObj,
				filterWithLicensesUnits,
				clear,
				getUrlPreference,
				clientLicenses,
				hasHierarchyAccess,
				currentAddress,
				geolocation,
				selectingUnit,
			}}
		>
			{children}
		</UserStoreContext.Provider>
	);
};

export function useUserStore() {
	const context = useContext(UserStoreContext);

	return context;
}
